import { exponentOf } from './currency';
import { allocateByWeights, convert, roundHalfAwayFromZero } from './money';
/**
 * Multicurrency arithmetic: the base-currency value of a foreign figure, and
 * the realised exchange difference on a settlement.
 *
 * ── THE ONE RULE ──
 * A document is owed in its own currency. A book is kept in one. Every row
 * in another currency also carries its base-currency value, written when the
 * row is written, at a rate the person saw - so nothing converts on read and
 * every aggregate is a SUM of base columns. The rate is ALWAYS "base units
 * per ONE unit of the foreign currency" - rupees per pound, 127.5000, the
 * direction FBIL quotes and `convert()` takes.
 *
 * The functions here are pure and shared by the browser (the composer's
 * "≈ ₹1,27,500.00 at 127.5000" preview, the payment drawer's third field)
 * and the server (what is stored), so the preview and the saved figure agree
 * to the paise - the property `composer-parity.ui.spec.ts` defends.
 */
/** Rates are stored to eight places, shown to four. */
export const RATE_SCALE = 8;
const RATE = /^\d+(\.\d+)?$/;
/**
 * A rate as it arrives - a string from a form or the database, or a number -
 * checked to be positive and finite. Returns undefined for a blank so a
 * caller can say "type the rate" rather than "NaN".
 */
export function parseRate(raw) {
    if (raw === null || raw === undefined)
        return undefined;
    const text = typeof raw === 'number' ? String(raw) : raw.trim().replace(/,/g, '');
    if (text === '')
        return undefined;
    if (!RATE.test(text))
        return undefined;
    const value = Number(text);
    return Number.isFinite(value) && value > 0 ? value : undefined;
}
/** A rate as the database and the wire carry it: a plain decimal string, eight places. */
export function formatRate(rate) {
    return rate.toFixed(RATE_SCALE);
}
/** A rate as a person reads it: four places, trailing zeros kept so 127.5000 reads as a rate. */
export function formatRateHuman(rate) {
    const value = typeof rate === 'number' ? rate : Number(rate);
    return value.toFixed(4);
}
/**
 * The base-currency value of a foreign amount at a rate, rounded once, half
 * away from zero. `convert()` with the direction fixed and named.
 */
export function toBase(m, base, rate) {
    if (m.currency === base)
        return m;
    return convert(m, base, rate);
}
/**
 * The rate two amounts imply - what the bank credited over what the customer
 * sent - to eight places. What a payment stores as its rate when both
 * figures are known, so a stale client-side rate can never disagree with
 * the cash.
 */
export function impliedRate(base, foreign) {
    if (foreign.minor <= 0)
        throw new Error('impliedRate() needs a positive foreign amount');
    const baseMajor = base.minor / 10 ** exponentOf(base.currency);
    const foreignMajor = foreign.minor / 10 ** exponentOf(foreign.currency);
    return Number((baseMajor / foreignMajor).toFixed(RATE_SCALE));
}
/**
 * The foreign amount a base amount buys at a rate - the third field when a
 * person typed rupees and a rate and left the pounds blank.
 */
export function fromBase(base, foreign, rate) {
    if (!Number.isFinite(rate) || rate <= 0)
        throw new Error('fromBase() needs a positive finite rate');
    const baseMajor = base.minor / 10 ** exponentOf(base.currency);
    const exp = exponentOf(foreign);
    return { minor: roundHalfAwayFromZero((baseMajor / rate) * 10 ** exp), currency: foreign };
}
/**
 * The booked base value of ONE allocation against a document, telescoped.
 *
 * `round(baseTotal × settledThrough / total) - round(baseTotal × settledBefore
 * / total)`, where `settledBefore` is what was allocated to the document
 * before this allocation and `settledThrough` includes it. The last
 * allocation absorbs the rounding, so the booked bases of a fully settled
 * document sum to `baseTotal` exactly and its FX difference is exactly
 * Σ base_amount − base_total. Rounding each allocation on its own would
 * leave a paise unaccounted for on one invoice in ten.
 *
 * At rate 1 (`baseTotal` equals `total` in value) this is the allocation
 * amount itself, which is the backfill and the regression property.
 */
export function bookedBase(baseTotal, total, settledBefore, settledThrough) {
    if (total.minor <= 0)
        return { minor: 0, currency: baseTotal.currency };
    const share = (settled) => roundHalfAwayFromZero((baseTotal.minor * settled) / total.minor);
    return { minor: share(settledThrough.minor) - share(settledBefore.minor), currency: baseTotal.currency };
}
/**
 * How a payment's base cash is split across what it settled.
 *
 * The settleable base value is the base cash plus the base value of what
 * was withheld (TDS, bank charges - money that discharged the invoice
 * without arriving). It is split across the allocations and the unapplied
 * remainder by their document-currency weights with `allocateByWeights`, so
 * base handed to invoices plus base left as credit equals base cash plus
 * withheld, to the paise. Returns one base amount per allocation, then the
 * unapplied share last.
 */
export function splitBaseAcross(settleableBase, allocations, unapplied) {
    const weights = [...allocations.map((a) => a.minor), unapplied.minor];
    if (weights.every((w) => w === 0)) {
        return { allocations: allocations.map(() => ({ minor: 0, currency: settleableBase.currency })), unapplied: settleableBase };
    }
    const parts = allocateByWeights(settleableBase, weights);
    return { allocations: parts.slice(0, -1), unapplied: parts[parts.length - 1] ?? { minor: 0, currency: settleableBase.currency } };
}
//# sourceMappingURL=fx.js.map